import * as Popover from "@radix-ui/react-popover";

import { useEffect, useMemo, useState } from "react";

import { Button } from "react-migration/components/Button";
import { MoveToStageForm } from "react-migration/domains/sites/components/MoveSavedSites/MoveToStageForm";
import { SelectProject } from "react-migration/domains/sites/components/MoveSavedSites/SelectBox";
import { getEditableDrawing } from "src/js/stores/drawing/actions/getEditableDrawing";
import { getSelectedGeometry } from "src/js/stores/drawing/actions/getSelectedGeometry";
import { removeDrawingFromLocalState } from "src/js/stores/drawing/actions/removeDrawingFromLocalState";
import { setSelectedGeometry } from "src/js/stores/drawing/actions/setSelectedGeometry";
import { siteStore } from "src/js/stores/site/store";
import { updateDrawing } from "src/js/stores/drawing/actions/updateDrawing";
import { useProjectsList } from "react-migration/domains/projects/hooks/useProjectsList";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

export function MoveToDropdown() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { selectedProjectId } = useSnapshot(siteStore);
  const { list: allProjects, loading } = useProjectsList();

  const selectedDrawing = getSelectedGeometry();
  const editableDrawing = getEditableDrawing();
  const drawing = selectedDrawing || editableDrawing;

  const [selectedProject, setSelectedProject] = useState<SelectProject | undefined>(undefined);

  const previousProject = useMemo(() => {
    const projectId = drawing?.workspaceId || selectedProjectId;
    if (!projectId) return undefined;
    return allProjects.find((candidate) => candidate.id === projectId);
  }, [allProjects, selectedProjectId, drawing?.workspaceId]);

  useEffect(() => {
    if (!previousProject) return;
    setSelectedProject(previousProject);
  }, [previousProject]);

  const handleCancel = () => {
    setIsOpen(false);
    setSelectedProject(previousProject);
  };

  const currentWorkspaceId = drawing?.workspaceId || selectedProjectId;
  const isMovingToAnotherWorkspace = selectedProject && selectedProject.id !== currentWorkspaceId;

  const handleMove = () => {
    if (selectedProject && drawing) {
      updateDrawing(drawing, true, { workspaceId: selectedProject.id });
      if (isMovingToAnotherWorkspace) {
        removeDrawingFromLocalState(drawing.id);
        setSelectedGeometry("");
      }
    }
    setIsOpen(false);
  };

  const moveDisabled = !selectedProject || loading;

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <Popover.Trigger asChild>
        <Button
          variant="tertiary"
          size="md"
          leadIcon="lt-share-forward-line"
          tailIcon="lt-arrow-down-s-line"
          data-testid="move-to-dropdown-trigger"
        >
          {t("drawings.move_to")}
        </Button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={5}
          className="bg-background-surface rounded-lg border border-border-action shadow-lg"
          data-testid="move-to-dropdown-content"
        >
          <MoveToStageForm
            title={t("drawings.move_to")}
            cancelLabel={t("general.cancel")}
            submitLabel={t("general.move")}
            projectSelect={{
              label: t("drawings.select_a_workspace"),
              projects: allProjects,
              selectedProject,
              onChange: setSelectedProject,
              loading,
            }}
            disabled={moveDisabled}
            onSubmit={handleMove}
            onCancel={handleCancel}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
